
import React, { useState, useCallback } from 'react';
import { FileUp, File, X, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Sidebar } from '@/components/Sidebar';
import PageTransition from '@/components/PageTransition';
import { useToast } from '@/hooks/use-toast';
import { AnimatedButton } from '@/components/AnimatedButton';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  progress: number;
  status: 'uploading' | 'complete' | 'error';
}

const Upload = () => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const { toast } = useToast();

  const generateUniqueId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  };

  const handleFileUpload = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles) return;

    // Convert FileList to array and process each file
    Array.from(selectedFiles).forEach(file => {
      // Check file type - only allow images
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Invalid file type",
          description: `${file.name} is not an image file.`,
          variant: "destructive",
        });
        return;
      }

      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: `${file.name} exceeds the 5MB size limit.`,
          variant: "destructive",
        });
        return;
      }

      const newFile: UploadedFile = {
        id: generateUniqueId(),
        name: file.name,
        size: file.size,
        type: file.type,
        progress: 0,
        status: 'uploading'
      };

      setFiles(prev => [...prev, newFile]);

      // Simulate upload progress
      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.floor(Math.random() * 10) + 5;
        
        if (progress >= 100) {
          clearInterval(interval);
          progress = 100;
          
          setFiles(prev => 
            prev.map(f => 
              f.id === newFile.id 
                ? { ...f, progress, status: 'complete' } 
                : f
            )
          );
          
          toast({
            title: "Upload complete",
            description: `${file.name} has been uploaded successfully.`,
          });
        } else {
          setFiles(prev => 
            prev.map(f => 
              f.id === newFile.id 
                ? { ...f, progress } 
                : f
            )
          );
        }
      }, 300);
    });
  }, [toast]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileUpload(e.dataTransfer.files);
  }, [handleFileUpload]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileUpload(e.target.files);
  }, [handleFileUpload]);

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(file => file.id !== id));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' bytes';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />
      <div className="flex-1 overflow-y-auto">
        <PageTransition>
          <div className="p-6 md:p-8">
            <header className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold">Upload Images</h1>
              <p className="text-muted-foreground">Add important images to your emergency plan</p>
            </header>

            <div className="mb-8">
              <div 
                className={`
                  border-2 border-dashed rounded-xl p-8 text-center
                  transition-colors duration-200 cursor-pointer
                  ${isDragging 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:border-primary/50 hover:bg-muted/50'}
                `}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById('file-upload')?.click()}
              >
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <FileUp className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">Drag and drop your files here</h3>
                  <p className="text-muted-foreground mb-4">or click to browse your files</p>
                  <p className="text-sm text-muted-foreground mb-4">Accepted file types: JPG, PNG, GIF</p>
                  <p className="text-sm text-muted-foreground">Maximum file size: 5MB</p>
                  
                  <Input
                    id="file-upload"
                    type="file"
                    multiple
                    accept="image/*"
                    className="hidden"
                    onChange={handleInputChange}
                  />
                  
                  <AnimatedButton className="mt-4" size="sm">
                    Select Files
                  </AnimatedButton>
                </div>
              </div>
            </div>

            {files.length > 0 && (
              <div className="space-y-5 opacity-0 animate-fade-up">
                <h2 className="text-xl font-semibold mb-4">Uploaded Files</h2>
                <div className="space-y-4">
                  {files.map(file => (
                    <div 
                      key={file.id} 
                      className="bg-card border border-border rounded-lg p-4 flex items-center"
                    >
                      <div className="w-10 h-10 bg-primary/10 rounded-md flex items-center justify-center mr-4">
                        <File className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-medium text-sm truncate">{file.name}</h4>
                          <div className="flex items-center">
                            {file.status === 'complete' ? (
                              <CheckCircle2 className="h-4 w-4 text-green-500 mr-2" />
                            ) : file.status === 'error' ? (
                              <AlertTriangle className="h-4 w-4 text-red-500 mr-2" />
                            ) : null}
                            <span className="text-xs text-muted-foreground">{formatFileSize(file.size)}</span>
                          </div>
                        </div>
                        <div className="w-full">
                          {file.status === 'uploading' ? (
                            <div className="flex items-center">
                              <Progress value={file.progress} className="flex-1 h-1.5" />
                              <span className="ml-2 text-xs text-muted-foreground">{file.progress}%</span>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-muted-foreground">
                                {file.status === 'complete' ? 'Upload complete' : 'Upload failed'}
                              </span>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-6 w-6"
                                onClick={() => removeFile(file.id)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="pt-4 flex justify-end">
                  <Button variant="outline" className="mr-2">Cancel</Button>
                  <AnimatedButton>Save to Plan</AnimatedButton>
                </div>
              </div>
            )}
          </div>
        </PageTransition>
      </div>
    </div>
  );
};

export default Upload;
